import { Controller } from "@hotwired/stimulus"
import Sortable from "sortablejs"

// Wraps the form_fields list (a <tbody>) with Sortable.js so admins can
// drag-and-drop to reorder. On drop, POSTs the new id-order to the
// reorder endpoint; on failure, snaps back to the pre-drag order.
//
// Targets:
//   - list:   the sortable container (Sortable's parent element)
//   - status: small text node we update with "Saving order…" / "Saved" / etc.
//
// Values:
//   - url: the reorder endpoint (admin_form_form_fields_reorder_path)
//
// HTML contract (set by the view):
//   - Each draggable row carries data-id="<form_field_id>" and a drag handle
//     marked .drag-handle (Sortable only initiates drag from there, so the
//     row's other interactive elements like Edit/Delete remain clickable).
export default class extends Controller {
  static targets = ["list", "status"]
  static values = { url: String }

  connect() {
    if (!this.hasListTarget) return

    this.preDragOrder = this.currentOrder()
    this.sortable = Sortable.create(this.listTarget, {
      handle: ".drag-handle",
      animation: 150,
      ghostClass: "sortable-ghost",
      // Long-press to drag on touch devices (avoids accidental drags during
      // scroll). 200ms is the Sortable.js default-friendly value for mobile.
      delay: 200,
      delayOnTouchOnly: true,
      onStart: () => {
        this.preDragOrder = this.currentOrder()
      },
      onEnd: () => this.persist()
    })
  }

  disconnect() {
    if (this.sortable) {
      this.sortable.destroy()
      this.sortable = null
    }
  }

  // Read current row ids from the DOM — single source of truth at any moment.
  currentOrder() {
    return Array.from(this.listTarget.querySelectorAll("[data-id]"))
      .map((el) => el.dataset.id)
      .filter(Boolean)
  }

  async persist() {
    const ids = this.currentOrder()
    this.setStatus("Saving order…")

    const token = document.querySelector('meta[name="csrf-token"]')?.content

    try {
      const res = await fetch(this.urlValue, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": token || "",
          Accept: "application/json"
        },
        credentials: "same-origin",
        body: JSON.stringify({ ids })
      })

      if (!res.ok) {
        this.revertToPreDragOrder()
        this.setStatus("Couldn't save — order restored.", { error: true })
        return
      }

      this.setStatus("Saved.")
      this.preDragOrder = ids
    } catch (_e) {
      this.revertToPreDragOrder()
      this.setStatus("Network error — order restored.", { error: true })
    }
  }

  // Restore DOM order by physically reattaching rows in the pre-drag sequence.
  revertToPreDragOrder() {
    const rows = new Map(
      Array.from(this.listTarget.querySelectorAll("[data-id]")).map((el) => [el.dataset.id, el])
    )
    this.preDragOrder.forEach((id) => {
      const el = rows.get(id)
      if (el) this.listTarget.appendChild(el)
    })
  }

  setStatus(text, { error = false } = {}) {
    if (!this.hasStatusTarget) return
    this.statusTarget.textContent = text
    this.statusTarget.style.color = error ? "var(--color-danger)" : ""
    if (!error) {
      clearTimeout(this._fadeTimer)
      this._fadeTimer = setTimeout(() => {
        if (this.hasStatusTarget) this.statusTarget.textContent = ""
      }, 2000)
    }
  }
}
